import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { depuis, dateComplete } from "@/lib/dates";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Check, Trash2, X, Download, StickyNote, UserPlus, Bell, BellOff, Send, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function Prospects() {
  const { lang } = useI18n();
  const fr = lang !== "en";
  const [leads, setLeads] = useState([]);
  const [chargement, setChargement] = useState(true);
  // Note en cours d'édition : { id, texte }. Enregistrée à la sortie du champ.
  const [noteOuverte, setNoteOuverte] = useState(null);
  const [telOuvert, setTelOuvert] = useState(null);
  // Fiche en cours de correction : { id, nom, entreprise, courriel, telephone,
  // adresse }. Ouverte par le crayon, enregistrée explicitement — contrairement
  // à la note, on ne sauve pas à la sortie du champ : passer d'une case à
  // l'autre ferait autant d'enregistrements que de champs.
  const [edition, setEdition] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const [exportEnCours, setExportEnCours] = useState(false);
  // Statuts affichés (cochés). Par défaut tout est coché : on ne cache rien tant
  // que le couvreur n'a pas décidé de filtrer.
  const TOUS_STATUTS = ["en_attente", "en_relance", "accepte_a_faire", "accepte_termine", "refuse"];
  const [filtres, setFiltres] = useState(() => new Set(TOUS_STATUTS));
  // Prospects cochés pour une action groupée (par lead_id).
  const [selection, setSelection] = useState(() => new Set());
  // Ajout manuel d'un prospect : le widget n'etait pas le seul chemin possible.
  const [formOuvert, setFormOuvert] = useState(false);
  const [creation, setCreation] = useState(false);
  const VIDE = { nom: "", telephone: "", courriel: "", adresse: "", note: "" };
  const [nouveau, setNouveau] = useState(VIDE);
  const champ = "bg-field border-line-strong rounded-md focus:border-orange-500 focus-visible:ring-orange-500";
  // Droit a la relance AUTOMATIQUE (forfait Trio et plus). La relance manuelle,
  // elle, est offerte a tout le monde — d'ou le bouton sur chaque prospect.
  const [abo, setAbo] = useState(null);
  const [envoiRelance, setEnvoiRelance] = useState("");

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data.leads || []);
    } catch {
      toast.error(fr ? "Chargement impossible" : "Could not load");
    } finally {
      setChargement(false);
    }
  }, [fr]);

  useEffect(() => {
    charger();
    api.get("/subscription").then(({ data }) => setAbo(data)).catch(() => {});
  }, [charger]);

  // Statuts du suivi commercial d'un prospect.
  const STATUTS = [
    ["en_attente", fr ? "En attente" : "Pending", "text-ink-soft"],
    ["en_relance", fr ? "En relance" : "Following up", "text-amber-700"],
    ["accepte_a_faire", fr ? "Accepté — à faire" : "Accepted — to do", "text-sky-400"],
    ["accepte_termine", fr ? "Accepté — terminé" : "Accepted — done", "text-emerald-400"],
    ["refuse", fr ? "Refusé" : "Refused", "text-red-400"],
  ];
  const clos = (s) => s === "accepte_termine" || s === "refuse";

  const changerStatut = async (id, statut) => {
    try {
      await api.put(`/leads/${id}/statut`, { statut });
      setLeads((L) => L.map((l) => (l.lead_id === id ? { ...l, statut, traite: clos(statut) } : l)));
    } catch {
      toast.error(fr ? "Échec" : "Failed");
    }
  };

  // Numéro du prospect, corrigeable en place. Même principe que la note :
  // enregistré à la sortie du champ, et seulement s'il a changé.
  const enregistrerTelephone = async (id, valeur) => {
    const avant = leads.find((l) => l.lead_id === id)?.telephone || "";
    setTelOuvert(null);
    if ((valeur || "").trim() === avant.trim()) return;
    try {
      await api.put(`/leads/${id}/telephone`, { telephone: valeur });
      setLeads((L) => L.map((l) => (l.lead_id === id ? { ...l, telephone: valeur.trim() } : l)));
      toast.success(fr ? "Numéro enregistré" : "Phone saved");
    } catch {
      toast.error(fr ? "Échec" : "Failed");
    }
  };

  // Fiche du prospect : nom, entreprise, courriel, cellulaire, adresse.
  // Le nom et l'adresse partent tels quels dans la soumission et dans les
  // courriels de suivi ; il faut donc pouvoir corriger une faute de frappe, ou
  // refaire la soumission au nom de quelqu'un d'autre, sans supprimer le
  // prospect et perdre son historique.
  const ouvrirEdition = (l) =>
    setEdition({
      id: l.lead_id,
      nom: l.nom || "",
      entreprise: l.entreprise || "",
      courriel: l.courriel || "",
      telephone: l.telephone || "",
      adresse: l.adresse || "",
    });

  const enregistrerFiche = async () => {
    if (!edition) return;
    const { id, ...champs } = edition;
    if (!champs.nom.trim() && !champs.courriel.trim() && !champs.telephone.trim()) {
      toast.error(fr ? "Il faut au moins un nom, un courriel ou un téléphone"
                     : "Enter at least a name, an email or a phone number");
      return;
    }
    setEnregistrement(true);
    try {
      const { data } = await api.put(`/leads/${id}`, champs);
      // On réécrit avec ce que le serveur a réellement retenu (courriel mis en
      // minuscules, espaces coupés) plutôt qu'avec ce qui a été tapé.
      setLeads((L) => L.map((l) => (l.lead_id === id ? {
        ...l, nom: data.nom, entreprise: data.entreprise, courriel: data.courriel,
        telephone: data.telephone, adresse: data.adresse,
      } : l)));
      setEdition(null);
      toast.success(fr ? "Fiche enregistrée" : "Lead saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || (fr ? "Échec" : "Failed"));
    } finally {
      setEnregistrement(false);
    }
  };

  // Note libre sur un prospect. Enregistrée à la sortie du champ (pas à chaque
  // frappe) : une requête par note plutôt qu'une par lettre.
  const enregistrerNote = async (id, texte) => {
    const avant = leads.find((l) => l.lead_id === id)?.note || "";
    setNoteOuverte(null);
    if ((texte || "").trim() === avant.trim()) return;
    try {
      await api.put(`/leads/${id}/note`, { note: texte });
      setLeads((L) => L.map((l) => (l.lead_id === id ? { ...l, note: texte.trim() } : l)));
      toast.success(fr ? "Note enregistrée" : "Note saved");
    } catch {
      toast.error(fr ? "Échec" : "Failed");
    }
  };

  // Export CSV : passe par axios pour envoyer le cookie de session, puis
  // déclenche le téléchargement depuis le blob reçu.
  const exporter = async () => {
    setExportEnCours(true);
    try {
      const { data } = await api.get("/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `prospects-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(fr ? "Export téléchargé" : "Export downloaded");
    } catch {
      toast.error(fr ? "Export impossible" : "Export failed");
    } finally {
      setExportEnCours(false);
    }
  };

  // Statut effectif d'un prospect (les anciens n'ont que `traite`).
  const statutDe = (l) => l.statut || (l.traite ? "accepte_termine" : "en_attente");

  // Prospects réellement affichés : ceux dont le statut est coché dans le filtre.
  const visibles = useMemo(
    () => leads.filter((l) => filtres.has(statutDe(l))),
    [leads, filtres]
  );

  const basculerFiltre = (statut) =>
    setFiltres((prev) => {
      const suivant = new Set(prev);
      suivant.has(statut) ? suivant.delete(statut) : suivant.add(statut);
      return suivant;
    });

  const basculerSelection = (id) =>
    setSelection((prev) => {
      const suivant = new Set(prev);
      suivant.has(id) ? suivant.delete(id) : suivant.add(id);
      return suivant;
    });

  // Sélection « tout » : porte sur les prospects VISIBLES seulement.
  const idsVisibles = visibles.map((l) => l.lead_id);
  const toutSelectionne = idsVisibles.length > 0 && idsVisibles.every((id) => selection.has(id));
  const basculerTout = () =>
    setSelection((prev) => {
      const suivant = new Set(prev);
      if (toutSelectionne) idsVisibles.forEach((id) => suivant.delete(id));
      else idsVisibles.forEach((id) => suivant.add(id));
      return suivant;
    });

  const supprimer = async (id) => {
    if (!window.confirm(fr ? "Supprimer ce prospect ?" : "Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((L) => L.filter((l) => l.lead_id !== id));
      setSelection((prev) => {
        const s = new Set(prev);
        s.delete(id);
        return s;
      });
      toast.success(fr ? "Prospect supprimé" : "Lead deleted");
    } catch {
      toast.error(fr ? "Échec" : "Failed");
    }
  };

  const ids = () => [...selection].filter((id) => idsVisibles.includes(id));

  const supprimerSelection = async () => {
    const cible = ids();
    if (!cible.length) return;
    if (!window.confirm(fr ? `Supprimer ${cible.length} prospect(s) ?` : `Delete ${cible.length} lead(s)?`)) return;
    try {
      await api.post("/leads/bulk", { ids: cible, action: "supprimer" });
      const aRetirer = new Set(cible);
      setLeads((L) => L.filter((l) => !aRetirer.has(l.lead_id)));
      setSelection(new Set());
      toast.success(fr ? `${cible.length} prospect(s) supprimé(s)` : `${cible.length} lead(s) deleted`);
    } catch {
      toast.error(fr ? "Échec" : "Failed");
    }
  };

  const statutSelection = async (statut) => {
    const cible = ids();
    if (!cible.length || !statut) return;
    try {
      await api.post("/leads/bulk", { ids: cible, action: "statut", statut });
      const set = new Set(cible);
      setLeads((L) => L.map((l) => (set.has(l.lead_id) ? { ...l, statut, traite: clos(statut) } : l)));
      setSelection(new Set());
      toast.success(fr ? "Statut mis à jour" : "Status updated");
    } catch {
      toast.error(fr ? "Échec" : "Failed");
    }
  };

  const argent = (v) => `${Number(v || 0).toLocaleString(fr ? "fr-CA" : "en-CA")} $`;

  if (chargement) {
    return (
      <Layout>
        <div className="flex justify-center py-24">
          <Loader2 className="h-7 w-7 animate-spin text-brand-ink" />
        </div>
      </Layout>
    );
  }

  // Relance automatique d'un prospect existant, coupee ou remise sans toucher
  // a son statut commercial.
  const basculerRelance = async (id, actif) => {
    try {
      await api.put(`/leads/${id}/relance`, { actif });
      setLeads((L) => L.map((l) => (l.lead_id === id ? { ...l, relance_ok: actif } : l)));
      toast.success(actif
        ? (fr ? "Relance réactivée" : "Follow-up resumed")
        : (fr ? "Relance arrêtée pour ce prospect" : "Follow-up stopped for this lead"));
    } catch {
      toast.error(fr ? "Échec" : "Failed");
    }
  };

  // Relance MANUELLE : envoie tout de suite le prochain suivi de la sequence.
  // Offerte sur tous les forfaits, y compris ceux sans envoi automatique.
  const relancerMaintenant = async (l) => {
    const nom = l.nom || l.courriel;
    if (!window.confirm(fr
      ? `Envoyer le prochain suivi à ${nom} maintenant ?`
      : `Send the next follow-up to ${nom} now?`)) return;
    setEnvoiRelance(l.lead_id);
    try {
      const { data } = await api.post(`/leads/${l.lead_id}/relancer`);
      setLeads((L) => L.map((x) => (x.lead_id === l.lead_id
        ? { ...x, relances: data.relances, statut: "en_relance" } : x)));
      toast.success(fr ? "Suivi envoyé" : "Follow-up sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || (fr ? "Échec" : "Failed"));
    } finally {
      setEnvoiRelance("");
    }
  };

  const creerProspect = async () => {
    if (!nouveau.nom.trim() && !nouveau.courriel.trim() && !nouveau.telephone.trim()) {
      toast.error(fr ? "Il faut au moins un nom, un courriel ou un téléphone"
                     : "Enter at least a name, an email or a phone number");
      return;
    }
    setCreation(true);
    try {
      await api.post("/leads", nouveau);
      toast.success(fr ? "Prospect ajouté" : "Lead added");
      setNouveau(VIDE);
      setFormOuvert(false);
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || (fr ? "Échec" : "Failed"));
    } finally {
      setCreation(false);
    }
  };

  return (
    <Layout>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter">
            {fr ? "Prospects" : "Leads"}
          </h1>
          <p className="text-ink-faint text-sm mt-1">
            {fr
              ? "Les demandes de votre estimateur, plus celles que vous ajoutez vous-même. Le code d'intégration est dans Configuration → Widget."
              : "Requests from your estimator, plus the ones you add yourself. The embed code lives in Settings → Widget."}
          </p>
        </div>
        <div className="flex items-center gap-3">
        {/* Ajout manuel : un appel reçu, une rencontre sur un chantier. Aucune
            mesure satellite, donc aucune estimation décomptée. */}
        <Button
          onClick={() => setFormOuvert((v) => !v)}
          data-testid="add-prospect"
          className="bg-brand hover:bg-brand/90 text-white rounded-md font-bold"
        >
          <UserPlus className="h-4 w-4 mr-2" /> {fr ? "Ajouter un prospect" : "Add a lead"}
        </Button>
        {/* Export : s'ouvre directement dans Excel (CSV, séparateur point-virgule). */}
        <Button
          onClick={exporter}
          disabled={exportEnCours || leads.length === 0}
          data-testid="export-prospects"
          variant="outline"
          className="border-line-strong rounded-md hover:bg-hover-strong hover:text-ink"
        >
          {exportEnCours
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <><Download className="h-4 w-4 mr-2" /> {fr ? "Exporter (CSV / Excel)" : "Export (CSV / Excel)"}</>}
        </Button>
        </div>
      </div>

      {abo && abo.relance_auto === false && (
        <div className="flex gap-3 rounded-xl border border-line bg-surface p-4 mb-6">
          <Send className="h-5 w-5 text-brand-ink shrink-0 mt-0.5" />
          <p className="text-sm text-ink-soft">
            {fr
              ? <>La <strong className="text-ink">relance automatique</strong> commence au forfait Trio. Vos modèles de suivi restent utilisables : le bouton <strong className="text-ink">Relancer</strong> envoie le prochain courriel de la séquence au prospect de votre choix, quand vous le décidez.</>
              : <>The <strong className="text-ink">automatic follow-up</strong> starts with the Trio plan. Your follow-up templates still work: the <strong className="text-ink">Follow up</strong> button sends the next email in the sequence to any lead, whenever you decide.</>}
          </p>
        </div>
      )}

      {formOuvert && (
        <div className="bg-surface border border-line rounded-xl shadow-carte p-5 mb-8">
          <h2 className="font-heading font-bold mb-1">{fr ? "Nouveau prospect" : "New lead"}</h2>
          <p className="text-xs text-ink-faint mb-4">
            {fr
              ? "Aucune mesure satellite : ce prospect ne touche pas à vos estimations du mois."
              : "No satellite measurement: this lead does not count against your monthly estimates."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Input data-testid="new-lead-nom" placeholder={fr ? "Nom" : "Name"}
              value={nouveau.nom} onChange={(e) => setNouveau({ ...nouveau, nom: e.target.value })} className={champ} />
            <Input data-testid="new-lead-tel" placeholder={fr ? "Cellulaire" : "Mobile"}
              value={nouveau.telephone} onChange={(e) => setNouveau({ ...nouveau, telephone: e.target.value })} className={champ} />
            <Input data-testid="new-lead-courriel" type="email" placeholder={fr ? "Courriel" : "Email"}
              value={nouveau.courriel} onChange={(e) => setNouveau({ ...nouveau, courriel: e.target.value })} className={champ} />
            <Input data-testid="new-lead-adresse" placeholder={fr ? "Adresse" : "Address"}
              value={nouveau.adresse} onChange={(e) => setNouveau({ ...nouveau, adresse: e.target.value })} className={champ} />
          </div>
          <Input data-testid="new-lead-note" placeholder={fr ? "Note (facultatif)" : "Note (optional)"}
            value={nouveau.note} onChange={(e) => setNouveau({ ...nouveau, note: e.target.value })}
            className={`${champ} mt-3`} />
          {/* Aucune case : un prospect saisi à la main n'entre jamais dans les
              envois automatiques — il n'a rempli aucun formulaire. */}
          <p className="text-xs text-ink-faint mt-4 max-w-2xl">
            {fr
              ? "Seuls les prospects mesurés par satellite peuvent être relancés automatiquement."
              : "Only leads measured by satellite can be followed up automatically."}
          </p>
          <div className="flex items-center gap-3 mt-4">
            <Button onClick={creerProspect} disabled={creation} data-testid="save-new-lead"
              className="bg-brand hover:bg-brand/90 text-white rounded-md font-bold">
              {creation ? <Loader2 className="h-4 w-4 animate-spin" /> : (fr ? "Ajouter" : "Add")}
            </Button>
            <button onClick={() => setFormOuvert(false)} className="text-sm text-ink-soft hover:text-ink">
              {fr ? "Annuler" : "Cancel"}
            </button>
          </div>
        </div>
      )}

      {/* --- prospects --- */}
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <h2 className="font-heading text-xl font-bold tracking-tight">
          {fr ? "Liste" : "List"}{" "}
          <span className="text-ink-faint font-normal">
            ({visibles.length}{visibles.length !== leads.length ? ` / ${leads.length}` : ""})
          </span>
        </h2>
        {/* Filtres : n'affiche que les statuts cochés. Tout coché par défaut. */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {STATUTS.map(([k, lbl, couleur]) => {
            const actif = filtres.has(k);
            return (
              <button
                key={k}
                data-testid={`filtre-${k}`}
                onClick={() => basculerFiltre(k)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                  actif ? "bg-hover-strong border-line-strong " + couleur : "bg-field border-line text-ink-faint opacity-60 hover:opacity-100"
                }`}
              >
                <span className={`h-3 w-3 rounded-[3px] border flex items-center justify-center ${actif ? "bg-orange-600 border-orange-600" : "border-line-strong"}`}>
                  {actif && <Check className="h-2.5 w-2.5 text-white" />}
                </span>
                {lbl}
              </button>
            );
          })}
        </div>
      </div>

      {/* Barre d'actions groupées : apparaît dès qu'un prospect visible est coché. */}
      {ids().length > 0 && (
        <div className="flex items-center gap-3 flex-wrap bg-surface border border-line-strong rounded-lg px-4 py-2.5 mb-3 shadow-carte">
          <span className="text-sm font-medium">
            {ids().length} {fr ? "sélectionné(s)" : "selected"}
          </span>
          <div className="h-4 w-px bg-line-strong" />
          <select
            data-testid="bulk-statut"
            value=""
            onChange={(e) => statutSelection(e.target.value)}
            className="bg-field border border-line-strong focus:border-orange-500 rounded-md px-2 py-1.5 text-xs outline-none cursor-pointer"
          >
            <option value="">{fr ? "Changer le statut…" : "Change status…"}</option>
            {STATUTS.map(([k, lbl]) => (
              <option key={k} value={k}>{lbl}</option>
            ))}
          </select>
          <Button
            onClick={supprimerSelection}
            data-testid="bulk-supprimer"
            className="h-8 bg-red-600/90 hover:bg-red-600 text-white rounded-md text-xs font-semibold"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {fr ? "Supprimer" : "Delete"}
          </Button>
          <button
            onClick={() => setSelection(new Set())}
            className="ml-auto text-ink-faint hover:text-ink"
            title={fr ? "Tout désélectionner" : "Clear selection"}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="bg-surface border border-line rounded-xl shadow-carte overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="border-b border-line text-ink-faint text-xs uppercase tracking-widest">
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  data-testid="select-tout"
                  checked={toutSelectionne}
                  onChange={basculerTout}
                  aria-label={fr ? "Tout sélectionner" : "Select all"}
                  className="h-4 w-4 accent-orange-600 cursor-pointer align-middle"
                />
              </th>
              <th className="text-left px-5 py-3 font-medium">{fr ? "Contact" : "Contact"}</th>
              <th className="text-left px-5 py-3 font-medium">{fr ? "Adresse" : "Address"}</th>
              <th className="text-left px-5 py-3 font-medium">{fr ? "Surface" : "Area"}</th>
              <th className="text-left px-5 py-3 font-medium">{fr ? "Fourchette" : "Range"}</th>
              <th className="text-left px-5 py-3 font-medium">{fr ? "Note" : "Note"}</th>
              <th className="text-left px-5 py-3 font-medium">{fr ? "Reçu" : "Received"}</th>
              <th className="text-right px-5 py-3 font-medium">{fr ? "Statut" : "Status"}</th>
              <th className="px-4 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((l) => (edition?.id === l.lead_id ? (
              /* Correction de la fiche : la ligne entière devient un formulaire.
                 Cinq champs à l'étroit dans une cellule de tableau seraient
                 illisibles, et la mesure comme la fourchette ne se corrigent
                 pas — ce sont des faits, pas des coordonnées. */
              <tr key={l.lead_id} className="border-b border-line last:border-0 bg-orange-500/[0.06]">
                <td colSpan={9} className="px-5 py-4">
                  <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                    <h3 className="font-heading font-bold text-sm">
                      {fr ? "Corriger la fiche" : "Edit lead"}
                    </h3>
                    <p className="text-xs text-ink-faint">
                      {fr
                        ? "Le nom et l'adresse partent tels quels dans la soumission et dans les suivis."
                        : "The name and address go out as-is in the quote and in the follow-ups."}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <Input data-testid={`edit-nom-${l.lead_id}`} autoFocus
                      placeholder={fr ? "Nom" : "Name"} value={edition.nom}
                      onChange={(e) => setEdition({ ...edition, nom: e.target.value })} className={champ} />
                    <Input data-testid={`edit-entreprise-${l.lead_id}`}
                      placeholder={fr ? "Entreprise (facultatif)" : "Company (optional)"} value={edition.entreprise}
                      onChange={(e) => setEdition({ ...edition, entreprise: e.target.value })} className={champ} />
                    <Input data-testid={`edit-tel-${l.lead_id}`}
                      placeholder={fr ? "Cellulaire" : "Mobile"} value={edition.telephone}
                      onChange={(e) => setEdition({ ...edition, telephone: e.target.value })} className={champ} />
                    <Input data-testid={`edit-courriel-${l.lead_id}`} type="email"
                      placeholder={fr ? "Courriel" : "Email"} value={edition.courriel}
                      onChange={(e) => setEdition({ ...edition, courriel: e.target.value })} className={champ} />
                    <Input data-testid={`edit-adresse-${l.lead_id}`}
                      placeholder={fr ? "Adresse" : "Address"} value={edition.adresse}
                      onChange={(e) => setEdition({ ...edition, adresse: e.target.value })}
                      className={`${champ} lg:col-span-2`} />
                  </div>
                  {/* Un désabonnement appartient à la personne, pas à la fiche :
                      changer l'adresse courriel ne remet personne dans les envois. */}
                  {l.desabonne && (
                    <p className="text-xs text-ink-faint mt-3">
                      {fr
                        ? "Ce prospect s'est retiré des suivis automatiques. Changer le courriel ne l'y remet pas."
                        : "This lead opted out of automatic follow-ups. Changing the email does not put them back in."}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-4">
                    <Button onClick={enregistrerFiche} disabled={enregistrement}
                      data-testid={`edit-save-${l.lead_id}`}
                      className="bg-brand hover:bg-brand/90 text-white rounded-md font-bold">
                      {enregistrement ? <Loader2 className="h-4 w-4 animate-spin" /> : (fr ? "Enregistrer" : "Save")}
                    </Button>
                    <button onClick={() => setEdition(null)} data-testid={`edit-cancel-${l.lead_id}`}
                      className="text-sm text-ink-soft hover:text-ink">
                      {fr ? "Annuler" : "Cancel"}
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              <tr key={l.lead_id} className={`border-b border-line last:border-0 ${selection.has(l.lead_id) ? "bg-orange-500/[0.06]" : ""} ${clos(statutDe(l)) ? "opacity-45" : ""}`}>
                <td className="px-4 py-4 align-top">
                  <input
                    type="checkbox"
                    data-testid={`select-${l.lead_id}`}
                    checked={selection.has(l.lead_id)}
                    onChange={() => basculerSelection(l.lead_id)}
                    className="h-4 w-4 accent-orange-600 cursor-pointer"
                  />
                </td>
                <td className="px-5 py-4">
                  <div className="font-medium flex items-center gap-2 flex-wrap">
                    {l.nom || "—"}
                    {l.type === "roofer" && (
                      <span
                        data-testid={`roofer-${l.lead_id}`}
                        title={fr ? "Couvreur qui a essayé l'outil sur son propre bâtiment" : "Roofer who tried the tool on their own building"}
                        className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-md bg-orange-500/15 text-brand-ink border border-orange-500/30"
                      >
                        {fr ? "Couvreur" : "Roofer"}
                      </span>
                    )}
                  </div>
                  {l.entreprise && (
                    <div className="text-xs text-ink-soft">{l.entreprise}</div>
                  )}
                  <div className="text-ink-faint text-xs">{l.courriel}</div>
                  {l.desabonne && (
                    <span data-testid={`desabonne-${l.lead_id}`} title={fr ? "Ce prospect s'est retiré des suivis automatiques" : "This lead opted out of automatic follow-ups"}
                      className="inline-block mt-1 px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-md bg-hover-strong text-ink-soft border border-line-strong">
                      {fr ? "Désabonné" : "Unsubscribed"}
                    </span>
                  )}
                  {/* Numéro corrigeable en place : le visiteur du widget le tape
                      vite et souvent mal, et un prospect pris au téléphone n'en
                      a parfois aucun au départ. Un clic ouvre le champ ; le lien
                      d'appel reste à côté pour ne pas perdre le clic-pour-appeler. */}
                  {telOuvert === l.lead_id ? (
                    <input
                      autoFocus
                      data-testid={`tel-input-${l.lead_id}`}
                      defaultValue={l.telephone || ""}
                      placeholder={fr ? "Cellulaire" : "Mobile"}
                      onBlur={(e) => enregistrerTelephone(l.lead_id, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                        if (e.key === "Escape") setTelOuvert(null);
                      }}
                      className="mt-1 w-36 bg-field border border-line-strong rounded-md px-2 py-1 text-xs text-ink outline-none focus:border-orange-500"
                    />
                  ) : (
                    <div className="flex items-center gap-2 mt-0.5">
                      {l.telephone && (
                        <a href={`tel:${l.telephone.replace(/[^\d+]/g, "")}`} className="text-brand-ink text-xs hover:underline">
                          {l.telephone}
                        </a>
                      )}
                      <button
                        data-testid={`tel-edit-${l.lead_id}`}
                        onClick={() => setTelOuvert(l.lead_id)}
                        className="text-xs text-ink-faint hover:text-brand-ink transition-colors"
                      >
                        {l.telephone ? (fr ? "modifier" : "edit") : (fr ? "+ cellulaire" : "+ mobile")}
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-5 py-4 text-ink-soft max-w-[260px]">
                  <div>{l.adresse}</div>
                  {(l.urgent || l.assurance || l.financement || l.delai || l.dependances > 0) && (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {l.urgent && <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-md bg-red-500/15 text-red-400 border border-red-500/30">{fr ? "Urgent" : "Urgent"}</span>}
                      {l.assurance && <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-md bg-sky-500/15 text-sky-400 border border-sky-500/30">{fr ? "Assurance" : "Insurance"}</span>}
                      {l.financement && <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">{fr ? "Financement" : "Financing"}</span>}
                      {l.delai && <span className="px-1.5 py-0.5 text-[10px] rounded-md bg-hover text-ink-soft border border-line-strong">{({ "asap": fr ? "Le plus tôt possible" : "ASAP", "1-2m": fr ? "1-2 mois" : "1-2 mo", "3-6m": fr ? "3-6 mois" : "3-6 mo", "aucune": fr ? "Sans date" : "No date" })[l.delai] || l.delai}</span>}
                      {l.dependances > 0 && <span className="px-1.5 py-0.5 text-[10px] rounded-md bg-orange-500/15 text-brand-ink border border-orange-500/30">+{l.dependances} {fr ? "cabanon/garage" : "shed/garage"}</span>}
                    </div>
                  )}
                </td>
                <td className="px-5 py-4 whitespace-nowrap text-ink-soft">
                  {/* Sans mesure — prospect saisi à la main, ou estimateur réglé
                      pour ne pas mesurer — la case reste vide. Un « 0 pi² » se
                      lirait comme un toit réellement mesuré à zéro. */}
                  {l.footprint_sqft == null
                    ? <span className="text-ink-faint">—</span>
                    : <>{Number(l.footprint_sqft).toLocaleString(fr ? "fr-CA" : "en-CA")} {fr ? "pi²" : "sq ft"}</>}
                </td>
                <td className="px-5 py-4 whitespace-nowrap">
                  {/* Un prospect couvreur n'a jamais de prix : le widget en mode
                      roofer ne lui en montre aucun. Idem sans mesure. */}
                  {l.type === "roofer" || l.estime_bas == null
                    ? <span className="text-ink-faint">—</span>
                    : <>{argent(l.estime_bas)} – {argent(l.estime_haut)}</>}
                </td>
                {/* Note : clic = édition, enregistrée à la sortie du champ. */}
                <td className="px-5 py-4 align-top min-w-[180px] max-w-[240px]">
                  {noteOuverte?.id === l.lead_id ? (
                    <textarea
                      autoFocus
                      data-testid={`note-edit-${l.lead_id}`}
                      value={noteOuverte.texte}
                      rows={3}
                      maxLength={2000}
                      onChange={(e) => setNoteOuverte({ id: l.lead_id, texte: e.target.value })}
                      onBlur={() => enregistrerNote(l.lead_id, noteOuverte.texte)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setNoteOuverte(null);
                        // Entrée enregistre ; Maj+Entrée fait un saut de ligne.
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.target.blur(); }
                      }}
                      placeholder={fr ? "Votre note…" : "Your note…"}
                      className="w-full bg-field border border-orange-500 rounded-md px-2 py-1.5 text-xs text-ink outline-none resize-y"
                    />
                  ) : (
                    <button
                      onClick={() => setNoteOuverte({ id: l.lead_id, texte: l.note || "" })}
                      data-testid={`note-${l.lead_id}`}
                      title={fr ? "Cliquer pour modifier" : "Click to edit"}
                      className="w-full text-left group"
                    >
                      {l.note ? (
                        <span className="text-xs text-ink-soft whitespace-pre-wrap break-words line-clamp-4 group-hover:text-ink">
                          {l.note}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-ink-faint group-hover:text-brand-ink">
                          <StickyNote className="h-3.5 w-3.5" /> {fr ? "Ajouter" : "Add"}
                        </span>
                      )}
                    </button>
                  )}
                </td>
                <td className="px-5 py-4 text-ink-faint whitespace-nowrap">
                  <span data-testid={`recu-${l.lead_id}`} title={dateComplete(l.created_at, lang)}>
                    {depuis(l.created_at, lang)}
                  </span>
                </td>
                <td className="px-5 py-4 text-right">
                  <select
                    data-testid={`statut-${l.lead_id}`}
                    value={l.statut || (l.traite ? "accepte_termine" : "en_attente")}
                    onChange={(e) => changerStatut(l.lead_id, e.target.value)}
                    className={`bg-field border border-line-strong focus:border-orange-500 rounded-md px-2 py-1.5 text-xs outline-none cursor-pointer ${
                      (STATUTS.find(([k]) => k === (l.statut || (l.traite ? "accepte_termine" : "en_attente"))) || [])[2] || "text-ink-soft"
                    }`}
                  >
                    {STATUTS.map(([k, lbl]) => (
                      <option key={k} value={k} className="text-ink">{lbl}</option>
                    ))}
                  </select>
                  {(l.relances || 0) > 0 && (
                    <div className="text-[10px] text-ink-faint mt-1">
                      {l.relances} {fr ? (l.relances > 1 ? "suivis envoyés" : "suivi envoyé") : (l.relances > 1 ? "follow-ups sent" : "follow-up sent")}
                      {l.desabonne && (fr ? " · arrêté" : " · stopped")}
                    </div>
                  )}
                  {/* Sortir un prospect des envois SANS mentir sur son statut :
                      fermer le dossier était jusqu'ici le seul moyen de faire
                      taire les courriels. Un désabonnement du prospect lui-même
                      ne se rouvre pas d'ici — c'est sa décision, pas celle du
                      couvreur. */}
                  {/* Relance MANUELLE : offerte sur tous les forfaits. Le
                      bouton disparait quand le prospect s'est desabonne ou n'a
                      pas de courriel — rien a envoyer. */}
                  {!l.desabonne && (l.courriel || "").trim() !== "" && (
                    <button
                      onClick={() => relancerMaintenant(l)}
                      disabled={envoiRelance === l.lead_id}
                      data-testid={`relancer-${l.lead_id}`}
                      title={fr ? "Envoyer le prochain suivi maintenant" : "Send the next follow-up now"}
                      className="mt-1.5 mr-3 inline-flex items-center gap-1 text-[10px] text-brand-ink hover:underline disabled:opacity-50"
                    >
                      {envoiRelance === l.lead_id
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <Send className="h-3 w-3" />}
                      {fr ? "Relancer" : "Follow up"}
                    </button>
                  )}
                  {l.footprint_sqft == null ? (
                    <span className="mt-1.5 block text-[10px] text-ink-faint"
                      title={fr ? "Sans mesure satellite : relance manuelle seulement"
                                : "No satellite measurement: manual follow-up only"}>
                      {fr ? "Relance manuelle" : "Manual follow-up"}
                    </span>
                  ) : !l.desabonne && (
                    <button
                      onClick={() => basculerRelance(l.lead_id, l.relance_ok === false)}
                      data-testid={`relance-${l.lead_id}`}
                      title={l.relance_ok === false
                        ? (fr ? "Remettre dans la relance automatique" : "Put back in automatic follow-up")
                        : (fr ? "Retirer de la relance automatique" : "Remove from automatic follow-up")}
                      className={`mt-1.5 inline-flex items-center gap-1 text-[10px] transition-colors ${
                        l.relance_ok === false ? "text-ink-faint hover:text-ink" : "text-brand-ink hover:underline"
                      }`}
                    >
                      {l.relance_ok === false
                        ? <><BellOff className="h-3 w-3" /> {fr ? "Relance arrêtée" : "Follow-up off"}</>
                        : <><Bell className="h-3 w-3" /> {fr ? "Relance active" : "Follow-up on"}</>}
                    </button>
                  )}
                </td>
                <td className="px-4 py-4 text-right align-top">
                  <div className="flex items-center justify-end gap-0.5">
                    <button
                      onClick={() => ouvrirEdition(l)}
                      data-testid={`modifier-${l.lead_id}`}
                      title={fr ? "Corriger la fiche" : "Edit lead"}
                      className="p-1.5 text-ink-faint hover:text-brand-ink transition-colors"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => supprimer(l.lead_id)}
                      data-testid={`supprimer-${l.lead_id}`}
                      title={fr ? "Supprimer" : "Delete"}
                      className="p-1.5 text-ink-faint hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            )))}
            {!visibles.length && (
              <tr>
                <td colSpan={9} className="px-5 py-12 text-center text-ink-faint">
                  {leads.length
                    ? (fr ? "Aucun prospect avec ces statuts. Ajustez les filtres." : "No leads with these statuses. Adjust the filters.")
                    : (fr ? "Aucun prospect pour l'instant. Activez l'estimateur et placez-le sur votre site." : "No leads yet. Enable the estimator and add it to your site.")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
